import { type TimelineFeatures, useFeatures } from "@expert/features";
import type { GaiaSubscription, GaiaWebsocketContext, SubscriptionStatus } from "@expert/gaia";
import { useGaiaStore, useGaiaWebsocket } from "@expert/gaia";
import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import { useFetchOutageInfo } from "@expert/solve-tools";
import { solveSessionClosed, solveSessionStarted, useTimelineStore } from "@soluto-private/expert-workspace-timeline";
import pRetry from "p-retry";
import { useEffect, useRef } from "react";
import { isChatTask, sdkEventBus } from "@/sdk";
import type { SessionInstance } from "@/sdk/sessions/session";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { userCache } from "@/shared-utils";
import { getChatExpertAssistEnabled } from "@/timeline/utils/chatExpertAssistFeatureFlag";

const subscriptionTimeoutDuration = 2000;
const subscriptionRetryCount = 3;

interface SessionLifecycleProps {
    features: Pick<TimelineFeatures, "isProactiveEnabled">;
    partner: Partner;
    sessionId: string;
    websocketObj: GaiaWebsocketContext | null;
    withCustomer: boolean;
    logger: Logger;
    chatId?: string;
}

type SubscribeProps = {
    websocketObj: GaiaWebsocketContext | null;
} & Omit<GaiaSubscription, "sendJsonMessage" | "timeout">;

/**
 * @param logger Logger instance for logging events and errors
 * @description Currently, this hook is CHAT ONLY.
 * - Subscribes and unsubscribes chat sessions to GAIA websocket events.
 * - Handles session lifecycle events (start/end) and updates session state.
 * - Automatically resubscribes to active sessions after page refresh or websocket reconnect.
 */
export const useChatExpertAssistSessions = (logger: Logger) => {
    const { features } = useFeatures<TimelineFeatures>();
    const websocketObj = useGaiaWebsocket();
    const { isWsAuthenticated, subscriptionStatus } = useGaiaStore();
    const isSessionStoreHydrated = useSessionStore.persist.hasHydrated();
    const { fetchOutageInfo } = useFetchOutageInfo(logger);
    const ssoAccessToken = userCache.ssoAccessToken ?? "";

    /**
     * Track which sessions have already been started to prevent double subscription.
     * This prevents a race condition within the useEffect due to the dependency on `subscriptionStatus`
     * changing while the effect is running.
     */
    const startedSessionsRef = useRef<Set<string>>(new Set());

    /**
     * Set-up session lifecycle hooks for call start and call end events
     */
    useEffect(() => {
        const sessionChangedSubscription = sdkEventBus.on("session_changed", async ({ action, session }) => {
            const timelineState = useTimelineStore.getState().getTimelineState(session.id);
            const chatId = isChatTask(session.currentTask) ? session.currentTask.chatId : timelineState.chatId;
            const partner = session.currentTask?.partner ?? timelineState.partner;
            const topic = isChatTask(session.currentTask) ? session.currentTask.topic : undefined;
            const isChatExpertAssistEnabled = await getChatExpertAssistEnabled({ partner, topic });

            if (!isChatExpertAssistEnabled) {
                logger.debug(
                    { action, sessionId: session.id, chatId, partner },
                    "Chat expert assist feature is not enabled; ignoring execution",
                );
                return;
            }

            if (!chatId) {
                logger.warn(
                    { action, sessionId: session.id },
                    "Session changed event received for non-chat task; ignoring execution",
                );
                return;
            }

            if (action === "start") {
                void startSession({
                    chatId,
                    features,
                    partner,
                    sessionId: session.id,
                    websocketObj,
                    withCustomer: session.kind === "with-customer",
                    logger,
                });
                startedSessionsRef.current.add(session.id);
            }

            if (action === "end") {
                closeSession({ sessionId: session.id, websocketObj, chatId, partner, features, logger });
                startedSessionsRef.current.delete(session.id);
            }
        });

        return () => sessionChangedSubscription();
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    /**
     * Handle resubscribing to GAIA when no subscriptions currently exist and we have active sessions.
     * This is mainly use to handle the case where the expert refreshes the page and we need to
     * resubscribe to all active sessions. But this also covers the scenario where the expert
     * has no current chats, and a new one comes in while the expert does not have the timeline rendered.
     */
    useEffect(() => {
        async function handleResubscribe() {
            if (!isSessionStoreHydrated || !isWsAuthenticated) return;

            const activeUnsubscribedChatSessions = getActiveUnsubscribedChatSessions(
                useSessionStore.getState().sessions,
                subscriptionStatus,
                startedSessionsRef,
            );

            await Promise.all(
                activeUnsubscribedChatSessions.map(async (session) => {
                    if (!isChatTask(session.currentTask)) {
                        logger.warn(
                            {
                                sessionId: session.id,
                                taskId: session.currentTask?.id,
                            },
                            "Session is not a chat task; skipping subscription",
                        );
                        return;
                    }

                    const partner = session.currentTask.partner;
                    const topic = session.currentTask.topic;
                    const isChatExpertAssistEnabled = await getChatExpertAssistEnabled({ partner, topic });

                    if (!isChatExpertAssistEnabled) {
                        logger.debug(
                            { sessionId: session.id, chatId: session.currentTask.chatId, partner },
                            "Chat expert assist feature is not enabled; ignoring execution",
                        );
                        return;
                    }

                    void startSession({
                        chatId: session.currentTask.chatId,
                        features,
                        partner,
                        sessionId: session.id,
                        websocketObj,
                        withCustomer: session.kind === "with-customer",
                        logger,
                    });
                    startedSessionsRef.current.add(session.id);
                }),
            );
        }

        void handleResubscribe();
        // oxlint-disable-next-line exhaustive-deps
    }, [isWsAuthenticated, subscriptionStatus, isSessionStoreHydrated]);

    useEffect(() => {
        if (!features.isOutageAlertInfoEnabled) return;

        const outageSubscription = sdkEventBus.on("session_changed", ({ action, session }) => {
            if (action === "start" && ssoAccessToken) {
                const timelineState = useTimelineStore.getState().getTimelineState(session.id);

                const chatId = isChatTask(session.currentTask) ? session.currentTask.chatId : timelineState.chatId;

                if (!chatId) return;

                void fetchOutageInfo(ssoAccessToken);
            }
        });

        return () => outageSubscription();
        // oxlint-disable-next-line exhaustive-deps
    }, [features.isOutageAlertInfoEnabled, ssoAccessToken]);
};

const getActiveUnsubscribedChatSessions = (
    sessions: SessionInstance[],
    subscriptionStatus: Record<string, SubscriptionStatus>,
    startedSessionsRef: React.RefObject<Set<string>>,
) => {
    return sessions.filter(
        (session) =>
            session.metadata.status === "active" &&
            isChatTask(session.currentTask) &&
            !startedSessionsRef.current.has(session.id) &&
            subscriptionStatus[session.id] !== "subscribed" &&
            subscriptionStatus[session.id] !== "subscribing",
    );
};

const startSession = async ({
    sessionId,
    websocketObj,
    withCustomer,
    chatId,
    partner,
    features,
    logger,
}: SessionLifecycleProps) => {
    solveSessionStarted({
        sessionId,
        chatId,
        withCustomer,
        partner,
        features,
        messageKeyOverride: "inChatCustomerInfo",
    });
    await subscribeSessionToGaia({ sessionId, chatId, websocketObj, partner, logger });
};

const subscribeSessionToGaia = async ({ sessionId, chatId, partner, websocketObj, logger }: SubscribeProps) => {
    try {
        await pRetry(
            async () => {
                await websocketObj?.subscribeSessionToGaia({
                    sessionId,
                    chatId,
                    partner,
                    sendJsonMessage: websocketObj.sendJsonMessage,
                    timeout: subscriptionTimeoutDuration,
                    logger,
                });
            },
            { minTimeout: subscriptionTimeoutDuration, factor: 1, retries: subscriptionRetryCount },
        );
    } catch (error) {
        logger.error(
            { error, module: "useChatExpertAssistSessions", sessionId, chatId },
            `Error subscribing session ${sessionId} to gaia with max retries (${subscriptionRetryCount})`,
        );
    }
};

const closeSession = ({
    chatId,
    features,
    logger,
    partner,
    sessionId,
    websocketObj,
}: Omit<SessionLifecycleProps, "withCustomer">) => {
    solveSessionClosed({ sessionId, chatId, partner, features });
    websocketObj?.unsubscribeSessionFromGaia({
        logger,
        sessionId,
        sendJsonMessage: websocketObj.sendJsonMessage,
    });
    useTimelineStore.getState().clearTimelineState(sessionId);
};
